// apps/web/src/features/commercial-proposals/components/SavedProductPhoto.tsx
//
// @aiPurpose read-only saved-photo preview for the authenticated proposal
//   surfaces (#1207): the drawer detail body and the internal customer-sheet
//   preview. Fetches the presigned GET for the bound photo and renders an
//   <img>; renders NOTHING on any failure (no broken-image leak), mirroring
//   the public sheet's render-or-nothing rule. Only mount this when a photo
//   is actually bound.
// @aiSecurity uses the AUTHENTICATED presigned-URL endpoint — never a public
//   route. The bare-<img> variant (no caption) is used inside the shared
//   customer-sheet photo slot so it matches the public sheet's framing.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { requestCommercialProposalPhotoDownloadUrl } from '../../../api/commercial-proposals.js';

function useSavedPhotoSrc(proposalId: string): [string | null, () => void] {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    requestCommercialProposalPhotoDownloadUrl(proposalId)
      .then((res) => {
        if (active) setSrc(res.downloadUrl);
      })
      .catch(() => {
        if (active) setSrc(null);
      });
    return () => {
      active = false;
    };
  }, [proposalId]);
  return [src, () => setSrc(null)];
}

/**
 * Bare saved-photo <img> (no caption / framing) for the shared customer-sheet
 * photo slot. Returns null until the presigned URL resolves and on failure.
 */
export function SavedProductPhoto({ proposalId }: { proposalId: string }) {
  const { t } = useTranslation('commercialProposals');
  const [src, clear] = useSavedPhotoSrc(proposalId);
  if (!src) return null;
  return (
    <img
      data-testid="commercial-proposals-drawer-photo"
      src={src}
      alt={t('editor.photo.alt')}
      onError={clear}
      style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

/**
 * Captioned saved-photo field used in the drawer detail body. Same fetch
 * behaviour; adds the localized "Product photo" label and card framing.
 */
export function SavedProductPhotoField({ proposalId }: { proposalId: string }) {
  const { t } = useTranslation('commercialProposals');
  const [src, clear] = useSavedPhotoSrc(proposalId);
  if (!src) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 2 }}>
        {t('drawer.fields.photo')}
      </div>
      <img
        data-testid="commercial-proposals-drawer-photo"
        src={src}
        alt={t('editor.photo.alt')}
        onError={clear}
        style={{
          display: 'block',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 'var(--r-2)',
          border: '1px solid var(--border-1)',
        }}
      />
    </div>
  );
}
